import type React from "react";
import { Component } from "react";
import ButtonComponent from "./cubeComponent/form/ButtonComponent";
import { BUTTON_TYPES } from "./cubeComponent/constants/globalVariable";
import * as Sentry from "@sentry/react";
import pageBreak from "../src/assets/icons/svg/pageBreak.svg";
import { withTranslation } from "@i18n";
import type { TranslatedString, TranslationKey } from "@i18n";
import { IGNORED_EXCEPTION_MESSAGES, isHoopsError, MEMORY_LIMIT_CRASH_MESSAGES } from "./sharedErrorConstants";
import { isDecimalError } from "@Helpers/decimalClean";
import { showToastNotification } from "./reducers/toastSlice";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import type { AppDispatch } from "@Reducers/index";
import GridPageLayout from "@CubeComponents/html/GridPageLayout";
import { FramedClassic } from "@CubeComponents/html/GridPageLayout/moduleLayoutContext";

/**
 * Props `withTranslation()` injects, plus the router `location` this boundary resets on — supplied
 * by the single mount at Router.tsx, which passes `useLocation()`.
 */
interface ErrorBoundaryProps {
  t: (key: TranslationKey, options?: Record<string, unknown>) => TranslatedString;
  children?: React.ReactNode;
  location?: unknown;
  dispatch?: AppDispatch;
  memoryLimitEnabled?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean | null;
}

/**
 * Minimal structural view of the arbitrary values thrown/rejected across the app that
 * `ignoreErrors` inspects. Every field is optional because `err` is `unknown` at runtime,
 * which lets the checks below read these fields type-safely without an inline `as` cast.
 */
interface InspectableError {
  message?: unknown;
  data?: { scStateFailure?: number };
  scFunction?: unknown;
}

function isObjectError(err: unknown): err is InspectableError {
  return err !== null && typeof err === "object";
}

function ignoreErrors(err: unknown) {
  const errorMessage = isObjectError(err) ? err.message : err;
  if (typeof errorMessage === "string" && IGNORED_EXCEPTION_MESSAGES.some((msg) => errorMessage.includes(msg))) {
    return true;
  } else if (
    isObjectError(err) &&
    Object.hasOwn(err, "scFunction") &&
    (err.data?.scStateFailure === 1 || !globalThis.location.href.includes("designviewer"))
  ) {
    return true;
  } else if (isHoopsError(err) && !globalThis.location.href.includes("designviewer")) {
    return true;
  }
  return false;
}
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private memoryLimitToastShown = false;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: null,
    };

    globalThis.addEventListener("unhandledrejection", (event) => {
      // `PromiseRejectionEvent.reason` is whatever was rejected, which the DOM lib declares `any`.
      // Held as `unknown`: `ignoreErrors` already takes `unknown` and narrows it itself.
      const error: unknown = event.reason;
      console.log(error);
      if (this.isMemoryLimitCrash(isObjectError(error) ? error.message : error)) {
        this.showMemoryLimitToast();
        return;
      }
      if (!ignoreErrors(error)) {
        // Bad money data thrown in an async loader (a form section, the contract BOQ, the billing tabs) fails the page
        // like a render error does, instead of leaving an empty list or a spinner that never stops.
        if (isDecimalError(error)) this.setState({ hasError: true });
        //TODO: uncomment below line after october month
        // this.setState({ hasError: true });
        Sentry.captureException(error);
      }
    });

    globalThis.addEventListener("error", (err) => {
      console.log(err);
      if (this.isMemoryLimitCrash(err.message)) {
        this.showMemoryLimitToast();
        return;
      }
      if (!ignoreErrors(err.message)) {
        if (err.filename) {
          /*
            Assuming third party libraries like clarity and freshchat donot allow getting filename due to cors issue
          */
          this.setState({
            hasError: true,
          });
        }
        Sentry.captureException(err);
      }
    });
  }

  static getDerivedStateFromError(err: unknown) {
    console.log(err);
    // `err` is whatever was thrown, not necessarily an Error: read `message` structurally. The
    // previous `(err as Error).message` additionally threw on a thrown `null`/`undefined`.
    const ignored = ignoreErrors(isObjectError(err) ? err.message : undefined);
    if (!ignored) {
      Sentry.captureException(err);
    }
    return {
      hasError: !ignored,
    };
  }

  isMemoryLimitCrash(message: unknown): boolean {
    if (this.props.memoryLimitEnabled !== true || typeof message !== "string") return false;
    return MEMORY_LIMIT_CRASH_MESSAGES.some((msg) => message.includes(msg));
  }

  showMemoryLimitToast() {
    if (this.memoryLimitToastShown) return;
    this.memoryLimitToastShown = true;
    this.props.dispatch?.(
      showToastNotification({ content: this.props.t("viewerMeshBudgetExhausted"), type: TOAST_TYPE_WARNING }),
    );
  }

  handlePageRefresh() {
    globalThis.location.reload();
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.props.location !== prevProps.location) {
      this.setState({ hasError: false });
      this.memoryLimitToastShown = false;
    }
  }

  override render() {
    if (this.state.hasError) {
      // Framed where the new shell draws the route in a DS body (FramedClassic: unframed, it was a white page with
      // DS ink in the dark theme); the classic chrome gives no frame, so the flag-off app is unchanged.
      return (
        <FramedClassic>
          <GridPageLayout showHeader={false}>
            <div className="h-full justify-center bg-white items-center flex gap-5 flex-col w-full">
              <div className="flex flex-col gap-2 items-center justify-center">
                <img src={pageBreak} alt="pageBreak" />

                <span className="font-semibol text-2xl">{this.props.t("An unexpected error occurred")}</span>
                <span>{this.props.t("Our team has been notified about this and is working on a resolution")}</span>
                <span>{this.props.t("Meanwhile please refresh and try again")}</span>
                <span>
                  {" "}
                  <ButtonComponent onClick={this.handlePageRefresh} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
                    {this.props.t("Refresh")}
                  </ButtonComponent>
                </span>
              </div>
            </div>
          </GridPageLayout>
        </FramedClassic>
      );
    }
    return this.props.children;
  }
}
// `withTranslation()` injects react-i18next's `t`/`i18n`/`tReady`, so the wrapped component's public
// props are just this boundary's own (`children` + `location`) with the injected `t` omitted. An
// explicit annotation is required here: the HOC's inferred return type can't be named without an
// internal `react-i18next/helpers` path (TS2742). Typed to `ErrorBoundaryProps` (single source of
// truth) rather than `any`.
const ErrorBoundaryWithTranslation: React.ComponentType<Omit<ErrorBoundaryProps, "t">> =
  withTranslation()(ErrorBoundary);
export default ErrorBoundaryWithTranslation;
